import {
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  Typography,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import GetAppIcon from "@material-ui/icons/GetApp";
import { Alert } from "@material-ui/lab";
import React, { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import styled from "styled-components";
import cuideoTheme from "../../../../../../containers/themes/defaultTheme";
import { apiGetCandidateAttachmentContent } from "../../api/getCandidateAttachmentContent";
import { TCandidateAttachment } from "../../types/candidateAttachment.types";
import {
  downloadBlob,
  getAttachmentPreviewKind,
} from "../../util/candidateAttachment.util";

const StyledDialog = styled(Dialog)`
  .MuiDialog-paper {
    height: 90vh;
  }
`;

const StyledHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 0.5rem 0.5rem 0.5rem 1rem;
  border-bottom: 1px solid rgba(0, 72, 120, 0.1);
`;

const StyledTitle = styled(Typography)`
  flex: 1 1 auto;
  color: ${cuideoTheme.palette.primary.main};
  font-size: 1.25rem;
  font-weight: 700;
`;

const StyledBody = styled.div`
  position: relative;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: auto;
  padding: 1rem;
`;

const StyledFrame = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
`;

const StyledImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
`;

const StyledVideo = styled.video`
  max-width: 100%;
  max-height: 100%;
`;

const StyledNotPreviewableText = styled(Typography)`
  margin-bottom: 1.5rem;
  color: ${cuideoTheme.palette.primary.main};
  font-size: 1.125rem;
  text-align: center;
`;

const StyledBigDownloadButton = styled(Button)`
  padding: 0.75rem 2.5rem;
  font-size: 1.125rem;
`;

const StyledAlert = styled(Alert)`
  margin: 1rem;
`;

const DEFAULT_FILE_NAME = "documento";

interface ICandidateAttachmentViewerDialogProps {
  open: boolean;
  candidateId: string;
  attachment: TCandidateAttachment | null;
  onClose: () => void;
}

/**
 * ECO-1416: previews a candidate attachment (PDF, image or video) and lets it be
 * downloaded. Any other type only offers the download.
 */
const CandidateAttachmentViewerDialog = (
  props: ICandidateAttachmentViewerDialogProps
) => {
  const { open, candidateId, attachment, onClose } = props;

  const { formatMessage } = useIntl();
  const [content, setContent] = useState<Blob | null>(null);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoadError, setHasLoadError] = useState(false);
  const [hasMediaError, setHasMediaError] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [hasDownloadError, setHasDownloadError] = useState(false);

  const attachmentId = attachment?.id ?? null;
  const previewKind = attachment
    ? getAttachmentPreviewKind(attachment.mimeType, attachment.title)
    : null;
  const fileName = attachment?.title || DEFAULT_FILE_NAME;
  // A file the browser cannot render (e.g. some image formats) is handled as not previewable
  const canPreview = previewKind !== null && !hasMediaError;

  // The content is only requested up front when it can be previewed
  useEffect(() => {
    setContent(null);
    setIsLoading(false);
    setHasLoadError(false);
    setHasMediaError(false);
    setHasDownloadError(false);

    if (!open || attachmentId === null || previewKind === null) {
      return;
    }

    let isCancelled = false;
    setIsLoading(true);
    apiGetCandidateAttachmentContent(candidateId, attachmentId)
      .then((blob) => {
        if (!isCancelled) {
          setContent(blob);
          setIsLoading(false);
        }
      })
      .catch(() => {
        if (!isCancelled) {
          setHasLoadError(true);
          setIsLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [open, candidateId, attachmentId, previewKind]);

  // The object URL lives as long as the content it points to
  useEffect(() => {
    if (!content) {
      setObjectUrl(null);
      return;
    }

    const url = URL.createObjectURL(content);
    setObjectUrl(url);

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [content]);

  const handleDownloadClick = async () => {
    if (attachmentId === null) {
      return;
    }
    if (content) {
      downloadBlob(content, fileName);
      return;
    }

    setIsDownloading(true);
    setHasDownloadError(false);
    try {
      downloadBlob(
        await apiGetCandidateAttachmentContent(candidateId, attachmentId),
        fileName
      );
    } catch (e) {
      setHasDownloadError(true);
    }
    setIsDownloading(false);
  };

  const handleMediaError = () => {
    setHasMediaError(true);
  };

  const renderPreview = () => {
    if (isLoading) {
      return <CircularProgress />;
    }
    if (hasLoadError) {
      return (
        <StyledAlert severity="error">
          <FormattedMessage
            id="CandidateAttachmentViewerDialog.No se ha podido cargar el documento"
            defaultMessage="No se ha podido cargar el documento"
          />
        </StyledAlert>
      );
    }
    if (!objectUrl) {
      return null;
    }
    if (previewKind === "pdf") {
      return <StyledFrame title={fileName} src={objectUrl} />;
    }
    if (previewKind === "image") {
      return (
        <StyledImage src={objectUrl} alt={fileName} onError={handleMediaError} />
      );
    }

    return (
      <StyledVideo
        src={objectUrl}
        controls
        onError={handleMediaError}
        data-testid="candidate-attachment-video"
      />
    );
  };

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      className="MuiCuideo-CandidateAttachmentViewerDialog"
      fullWidth
      maxWidth="lg"
    >
      <StyledHeader>
        <StyledTitle noWrap title={fileName}>
          {fileName}
        </StyledTitle>
        <IconButton
          color="primary"
          aria-label={formatMessage({
            id: "CandidateAttachmentViewerDialog.Descargar",
            defaultMessage: "Descargar",
          })}
          onClick={handleDownloadClick}
          disabled={isDownloading}
        >
          <GetAppIcon />
        </IconButton>
        <IconButton
          aria-label={formatMessage({
            id: "CandidateAttachmentViewerDialog.Cerrar",
            defaultMessage: "Cerrar",
          })}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      </StyledHeader>
      {hasDownloadError && (
        <StyledAlert severity="error">
          <FormattedMessage
            id="CandidateAttachmentViewerDialog.No se ha podido descargar el documento"
            defaultMessage="No se ha podido descargar el documento"
          />
        </StyledAlert>
      )}
      <StyledBody>
        {canPreview ? (
          renderPreview()
        ) : (
          <>
            <StyledNotPreviewableText>
              <FormattedMessage
                id="CandidateAttachmentViewerDialog.Este tipo de archivo no se puede visualizar"
                defaultMessage="Este tipo de archivo no se puede visualizar"
              />
            </StyledNotPreviewableText>
            <StyledBigDownloadButton
              variant="contained"
              color="primary"
              size="large"
              startIcon={
                isDownloading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <GetAppIcon />
                )
              }
              onClick={handleDownloadClick}
              disabled={isDownloading}
            >
              <FormattedMessage
                id="CandidateAttachmentViewerDialog.Descargar"
                defaultMessage="Descargar"
              />
            </StyledBigDownloadButton>
          </>
        )}
      </StyledBody>
    </StyledDialog>
  );
};

export default CandidateAttachmentViewerDialog;
